import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material'
import React from 'react'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import { inject } from 'mobx-react'
import { IStores } from 'src/Stores'
import _ from 'lodash'
import { InboxActionSummary } from 'src/viewModels/workflow/InboxActionSummary'
import { completeValidation, processAction } from 'src/services/WorkflowService'
import { ObjectiveProcessAction } from 'src/viewModels/workflow/ObjectiveProcessAction'
import { Warning } from '@mui/icons-material'
import { ClaimCustomForm } from 'src/utils/ClaimCustomForm'
import { OptionsObject } from 'notistack'
import { DuplicateClaimSummary } from 'src/viewModels/workflow/DuplicateClaimSummary'
import { BillStatusNumber } from 'src/utils/BillStatusNumber'
import moment from 'moment'
import { ILocation } from 'src/Definitions'

interface ICompleteWithContractTBDModalProps {
  isOpen: boolean
  closeModal: () => void
  action: InboxActionSummary
  rerouteToPath?: (path: string) => void
  inboxItemId: number
  inboxId: string
  associatedItemId: string
  data: ObjectiveProcessAction
  actionList: InboxActionSummary[]
  selectedLocation?: ILocation
  sendNotification?: (
    key: string,
    message: string,
    options?: OptionsObject,
    information?: string | undefined
  ) => void
  handleProcessActionFinish?: (selectedAction?: InboxActionSummary) => void
}

class CompleteWithContractTBDModal extends React.Component<ICompleteWithContractTBDModalProps> {
  public state = {
    errors: new Array<string>(),
    warnings: new Array<string>(),
    enableContractTBD: false,
    executed: false,
    tbdActionId: '',
    duplicateClaim: undefined as unknown as DuplicateClaimSummary,
    isLoading: true,
    comments: '',
  }

  public componentDidMount(): void {
    this.setState({ isLoading: true })
    var tbdComplete = this.props.actionList.find(
      (x) =>
        x.customFormName == ClaimCustomForm.InitialComplete &&
        x.statusId == BillStatusNumber.ContractTBD &&
        x.id != this.props.action.id
    )
    if (tbdComplete == null) {
      this.props.sendNotification!(
        '404',
        'This action is incorrectly configured. Please contact your administrator.',
        {
          variant: 'error',
        }
      )
      this.close()
    } else {
      if (this.props.data.claimData) {
        completeValidation(
          this.props.associatedItemId,
          false,
          this.props.data.claimData
        ).then((result) => {
          var missingContracts =
            this.props.data.claimData?.q24?.some((x) => x.contractAmount == undefined) ??
            true
          this.setState({
            duplicateClaim: result.duplicateClaimSummary,
            errors: result.errors,
            enableContractTBD: missingContracts,
            tbdActionId: tbdComplete?.id.toString(),
            isLoading: false,
            warnings: result.warnings,
          })
        })
      } else {
        this.props.sendNotification!(
          '404',
          'Claim data is missing, please refresh the page.',
          {
            variant: 'error',
          }
        )
        this.close()
      }
    }
  }

  private async save() {
    var action = this.props.action
    this.props.data.comment = this.state.comments

    if (this.state.errors.length > 0) {
      this.setState({ executed: false })
      return
    }

    processAction(
      this.props.data,
      this.state.enableContractTBD ? this.state.tbdActionId : action!.id.toString(),
      this.props.inboxItemId.toString()
    )
      .then(() => {
        this.props.handleProcessActionFinish!(action)
        this.close()
      })
      .catch(() => {
        this.setState({ executed: false, isLoading: false })
        this.close()
      })
  }

  private close = () => {
    this.setState({
      errors: new Array<string>(),
      enableContractTBD: false,
      executed: false,
      tbdActionId: '',
      isLoading: false,
    })
    this.props.closeModal()
  }

  private getProviderTypeName(typeId: string) {
    const providerType = this.props.selectedLocation?.providerTypes?.find(
      (x) => x.id === typeId
    )
    if (providerType) {
      return providerType.abbreviation
    }
    return 'undefined'
  }

  public renderDuplicate() {
    return (
      <Grid container item xs={12}>
        <Typography style={{ width: '100%' }}>
          {this.state.duplicateClaim.duplicateClaimCount == 1 ? (
            <>
              <strong>A claim</strong> already exists that matches{' '}
            </>
          ) : (
            <>
              <strong>
                {this.state.duplicateClaim.duplicateClaimCount + ' '}
                claims{' '}
              </strong>
              already exist that match{' '}
            </>
          )}
          the Provider Group, Date of Service, and EOC Number on this claim. Click{' '}
          {this.state.enableContractTBD ? 'Contract TBD' : 'Execute'} to continue saving
          this claim or click Close to return to the current claim.
        </Typography>
        <Grid container direction="row" item xs={12}>
          <Grid container direction="column" spacing={1} xs={6}>
            <h1>
              Previous
              {this.state.duplicateClaim.duplicateClaimCount == 1 ? ' Claim' : ' Claims'}
            </h1>
            <Grid item>
              <Grid container direction="row" spacing={2}>
                <Grid item>
                  <strong>Patient Name:</strong>
                </Grid>
                <Grid item>
                  <span>{this.state.duplicateClaim.patientName}</span>
                </Grid>
              </Grid>
            </Grid>
            <Grid item container direction="row" spacing={2}>
              <Grid item>
                <strong>EOC/Legacy:</strong>
              </Grid>
              <Grid item xs={9}>
                <span>{this.state.duplicateClaim.duplicateEOCLegacyGroup}</span>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container direction="row" spacing={2}>
                <Grid item>
                  <strong>Body Part:</strong>
                </Grid>
                <Grid item>
                  <span>{this.state.duplicateClaim.bodyPart}</span>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container direction="row" spacing={2}>
                <Grid item>
                  <strong>Provider Group:</strong>
                </Grid>
                <Grid item>
                  <span>{this.state.duplicateClaim.providerGroup}</span>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container direction="row" spacing={2}>
                <Grid item>
                  <strong>Provider Type:</strong>
                </Grid>
                <Grid item>
                  <span>{this.state.duplicateClaim.providerTypeList}</span>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container direction="row" spacing={2}>
                <Grid item>
                  <strong>Date of Service:</strong>
                </Grid>
                <Grid item>
                  <span>
                    {moment(this.state.duplicateClaim.dateOfService).format('MM/DD/YYYY')}
                  </span>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid container direction="column" spacing={1} xs={6}>
            <h1>Current Claim</h1>
            <Grid item>
              <Grid container direction="row" spacing={2}>
                <Grid item>
                  <strong>Patient Name:</strong>
                </Grid>
                <Grid item>
                  <span>{this.state.duplicateClaim.patientName}</span>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container direction="row" spacing={2}>
                <Grid item>
                  <strong>EOC/Legacy:</strong>
                </Grid>
                <Grid item>
                  <span>{this.state.duplicateClaim.newEOCLegacy}</span>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container direction="row" spacing={2}>
                <Grid item>
                  <strong>Body Part:</strong>
                </Grid>
                <Grid item>
                  <span>{this.state.duplicateClaim.bodyPart}</span>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container direction="row" spacing={2}>
                <Grid item>
                  <strong>Provider Group:</strong>
                </Grid>
                <Grid item>
                  <span>{this.state.duplicateClaim.providerGroup}</span>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container direction="row" spacing={2}>
                <Grid item>
                  <strong>Provider Type:</strong>
                </Grid>
                <Grid item>
                  <span>
                    {this.getProviderTypeName(
                      this.props.data.claimData?.providerTypeId ?? ''
                    )}
                  </span>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container direction="row" spacing={2}>
                <Grid item>
                  <strong>Date of Service:</strong>
                </Grid>
                <Grid item>
                  <span>
                    {moment(this.props.data.claimData?.dateOfService).format(
                      'MM/DD/YYYY'
                    )}
                  </span>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  public render() {
    var modalTitle = this.props.action.actionName.toUpperCase()

    return (
      <>
        <Dialog
          key={this.props.action.id}
          fullWidth={true}
          maxWidth="md"
          open={this.props.isOpen!}
          onClose={(_, reason) => {
            if (reason !== 'backdropClick') {
              this.close()
            }
          }}
        >
          <DialogTitle style={{ padding: 0 }}>
            <ResponsiveAppBar title={modalTitle} isModal={true} />
          </DialogTitle>
          <DialogContent style={{ padding: '12px 12px 8px' }}>
            {this.state.isLoading && (
              <div
                style={{
                  position: 'fixed',
                  display: 'flex',
                  width: '100%',
                  height: '100%',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  cursor: 'pointer',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: '1200',
                }}
              >
                <CircularProgress size={100} />
              </div>
            )}
            <Grid container direction="column" spacing={2}>
              <Grid item xs={12}>
                {this.state.errors.length > 0 || this.state.warnings?.length > 0 ? (
                  <>
                    {this.state.errors.length > 0 && (
                      <>
                        <h2 style={{ color: 'red' }}>Validation Errors</h2>
                        <ul style={{ color: 'red' }}>
                          {this.state.errors.map((error) => (
                            <li>{error}</li>
                          ))}
                        </ul>
                      </>
                    )}
                    {this.state.warnings?.length > 0 && (
                      <>
                        <h2>
                          <Warning
                            fontSize="large"
                            style={{ color: '#ffc107', verticalAlign: 'bottom' }}
                          />
                          &nbsp;Claim is ready to complete with warnings
                        </h2>
                        <ul>
                          {this.state.warnings.map((warning) => (
                            <li>{warning}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </>
                ) : this.state.isLoading ? (
                  <></>
                ) : (
                  <h2 style={{ color: 'green' }}>Success! Claim is ready to complete.</h2>
                )}
              </Grid>
              {this.state.duplicateClaim && this.renderDuplicate()}
              {this.props.action.displayComment && (
                <Grid item xs={12}>
                  <TextField
                    label="Comments"
                    name="notes"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    required={this.props.action.requireComment}
                    multiline
                    minRows="4"
                    disabled={this.state.errors.length > 0}
                    onChange={(event) => this.setState({ comments: event.target.value })}
                  />
                </Grid>
              )}
              {this.state.enableContractTBD && (
                <Grid item style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Warning
                    style={{
                      color: '#ffc107',
                      cursor: 'pointer',
                      paddingRight: '6px',
                    }}
                  />
                  Not all line items have contract amounts
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              color="primary"
              disabled={
                this.state.executed ||
                this.state.errors.length > 0 ||
                !this.state.enableContractTBD
              }
              onClick={() => {
                this.setState({ executed: true })
                this.save()
              }}
            >
              Contract TBD
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={
                this.state.executed ||
                this.state.errors.length > 0 ||
                this.state.enableContractTBD
              }
              onClick={() => {
                this.setState({ executed: true })
                this.save()
              }}
            >
              Execute
            </Button>
            <Button
              onClick={() => {
                this.close()
              }}
              data-cy="close"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </>
    )
  }
}

const InjectedCompleteWithContractTBDModal = inject<
  IStores,
  ICompleteWithContractTBDModalProps,
  Partial<ICompleteWithContractTBDModalProps>,
  any
>((stores: IStores) => ({
  rerouteToPath: stores.global.rerouteToPath,
  sendNotification: stores.notifications.sendNotification,
  selectedLocation: stores.locations.selectedEditClaimLocation,
}))(CompleteWithContractTBDModal)

export default InjectedCompleteWithContractTBDModal
